/**
 * OutreachFunnel — visualizes the outreach pipeline as a conversion funnel.
 */

import { useQuery } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useApi } from '@/hooks/use-api'

type FunnelStage = {
  readonly name: string
  readonly count: number
  readonly conversion_from_previous: number | null
}

type FunnelResponse = {
  readonly stages: readonly FunnelStage[]
}

// Cobalt → cyan gradient steps — matches the marketing-site blue/cyan palette
// and reads clearly against the obsidian background.
const STAGE_COLORS = [
  'bg-[var(--color-cobalt)]',
  'bg-[var(--color-cobalt-bright)]',
  'bg-[#4B8CFF]',
  'bg-[#3EB8E8]',
  'bg-[var(--color-cyan)]',
]

export function OutreachFunnel() {
  const apiFetch = useApi()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'funnel'],
    queryFn: async (): Promise<FunnelResponse> => {
      const res = await apiFetch<FunnelResponse>('/api/leads/analytics/funnel')
      if (!res.success || !res.data) return { stages: [] }
      return res.data
    },
    staleTime: 60_000,
  })

  const stages = data?.stages ?? []
  const maxCount = Math.max(1, ...stages.map((s) => s.count))

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Outreach Funnel</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">Outreach Funnel</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1">
          {stages.map((stage, i) => {
            const widthPct = Math.max(8, (stage.count / maxCount) * 100)
            return (
              <div key={stage.name} className="flex items-center gap-1 flex-1 min-w-0">
                <div className="flex flex-col items-center flex-1 min-w-0">
                  <span className="text-lg font-bold tabular-nums">{stage.count}</span>
                  <div
                    className={`w-full rounded-md ${STAGE_COLORS[i] ?? 'bg-gray-400'} transition-all`}
                    style={{ height: `${Math.max(8, widthPct * 0.8)}px` }}
                  />
                  <span className="mt-1 text-[10px] text-muted-foreground text-center truncate w-full">{stage.name}</span>
                </div>
                {i < stages.length - 1 && stage.conversion_from_previous !== null && stages[i + 1]?.conversion_from_previous !== null && (
                  <div className="flex flex-col items-center shrink-0 px-0.5">
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5">
                      {stages[i + 1]?.conversion_from_previous ?? 0}%
                    </Badge>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
